// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
    signInWithEmailAndPassword,
    setPersistence,
    browserSessionPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaUser } from 'react-icons/fa';
import { saveOfflineCredentials, verifyOfflineCredentials } from '../utils/authOffline';
import './Login.css';

// --- Sub-componente para el formulario de Inicio de Sesión ---
const SignInForm = ({ onSetView }) => {
    const navigate = useNavigate();
    // Cargar último email guardado al iniciar (solo email, sin contraseña)
    const [email, setEmail] = useState(() => {
        try {
            return localStorage.getItem('lastLoginEmail') || '';
        } catch {
            return '';
        }
    });
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(() => {
        // Cargar preferencia de "Recordar sesión" desde localStorage
        try {
            const saved = localStorage.getItem('rememberMe');
            return saved === 'true';
        } catch {
            return false;
        }
    });
    const { showNotification } = useNotification();
    const { setOfflineUser } = useAuth();
 
    const handleSignIn = async (e) => {
        e.preventDefault();

        // Guardar preferencia de "Recordar sesión"
        try {
            localStorage.setItem('rememberMe', rememberMe.toString());
        } catch (err) {
            console.log('[Login] No se pudo guardar preferencia rememberMe:', err);
        }

        // Para móvil, siempre usar persistencia local para mejor experiencia
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const persistenceType = (rememberMe || isMobile) ? browserLocalPersistence : browserSessionPersistence;

        try {
            await setPersistence(auth, persistenceType);
        } catch (err) {
            console.error('Login: setPersistence fallo', err);
            showNotification('No se pudo establecer la persistencia de sesión.', 'error');
            return;
        }

        const rawInput = email ? email.trim() : '';

        if (!rawInput) {
            showNotification('Por favor ingresa tu correo o número de teléfono.', 'warning');
            return;
        }

        let loginEmail = rawInput;

        // --- LÓGICA DE DETECCIÓN Y AUTENTICACIÓN (CORREO VS TELÉFONO) ---
        if (rawInput.includes('@')) {
            // Es un correo electrónico: limpiar espacios y minúsculas
            loginEmail = rawInput.toLowerCase();
            console.log('[Login] Intentando acceso directo con correo:', loginEmail);
        } else {
            // Es un número telefónico (extraer solo dígitos)
            const phoneDigits = rawInput.replace(/\D/g, '');
            if (phoneDigits.length === 10) {
                try {
                    console.log('[Login] Resolviendo teléfono vía Cloud Function getEmailByPhone:', phoneDigits);
                    const { functions } = await import('../firebase');
                    const { httpsCallable } = await import('firebase/functions');

                    const getEmailByPhone = httpsCallable(functions, 'getEmailByPhone');
                    const result = await getEmailByPhone({ phone: phoneDigits });

                    if (result?.data?.email) {
                        loginEmail = result.data.email.trim().toLowerCase();
                        console.log('[Login] Alias resuelto exitosamente a email:', loginEmail);
                    } else {
                        showNotification('No se encontró un correo asociado a este número telefónico.', 'error');
                        return;
                    }
                } catch (err) {
                    console.error('[Login] ERROR al resolver teléfono vía Cloud Function:', err);
                    const errMsg = err?.message || '';
                    if (err?.code === 'functions/not-found' || errMsg.includes('No se encontró')) {
                        showNotification('El número telefónico no está registrado en la plataforma.', 'error');
                    } else {
                        showNotification(errMsg || 'Error al verificar el teléfono. Ingresa con tu correo.', 'error');
                    }
                    return;
                }
            } else {
                showNotification('El formato ingresado no es válido. Ingresa tu correo o un teléfono de 10 dígitos.', 'warning');
                return;
            }
        }

        try {
            const cleanLoginEmail = loginEmail.trim().toLowerCase();
            const userCredential = await signInWithEmailAndPassword(auth, cleanLoginEmail, password);
            const user = userCredential.user;
 
            // Guardar credenciales para modo offline
            await saveOfflineCredentials(cleanLoginEmail, password, user.uid);

            // --- VERIFICAR SUSPENSIÓN ANTES DE PROSEGUIR ---
            try {
                const { db } = await import('../firebase');
                const { doc, getDoc } = await import('firebase/firestore');
                const { signOut } = await import('firebase/auth');

                const userDoc = await getDoc(doc(db, 'usuarios', user.uid));
                if (userDoc.exists() && userDoc.data().suspendido === true) {
                    await signOut(auth);
                    showNotification('Tu cuenta ha sido deshabilitada o suspendida. Contacta al administrador para más información.', 'error');
                    return;
                }
            } catch (suspensionErr) {
                console.warn('[Login] Verificación de suspensión diferida a AuthContext:', suspensionErr);
            }

            if (navigator.credentials && navigator.credentials.store && typeof PasswordCredential !== 'undefined') {
                try {
                    // eslint-disable-next-line no-undef
                    const credential = new PasswordCredential({
                        id: cleanLoginEmail,
                        password: password,
                        name: cleanLoginEmail.includes('@') ? cleanLoginEmail.split('@')[0] : cleanLoginEmail,
                    });
                    await navigator.credentials.store(credential);
                } catch (credError) {
                    console.log('[Login] No se pudo guardar con Credential API:', credError);
                }
            }

            try {
                localStorage.setItem('lastLoginEmail', cleanLoginEmail);
                if (rememberMe || isMobile) {
                    localStorage.setItem('autoLoginEnabled', 'true');
                }
            } catch (storageError) {
                console.log('[Login] No se pudo guardar en localStorage:', storageError);
            }
        } catch (error) {
            console.error('Login: signIn error', error);
            let msg = 'Ocurrió un error al iniciar sesión. Por favor intenta de nuevo.';

            if (error && error.code) {
                switch (error.code) {
                    case 'auth/wrong-password':
                        msg = 'La contraseña es incorrecta. Por favor verifica e intenta de nuevo.';
                        break;
                    case 'auth/missing-password':
                        msg = 'Por favor ingresa tu contraseña.';
                        break;
                    case 'auth/invalid-email':
                        msg = 'El formato ingresado no es válido. Verifica si es un correo o tu teléfono a 10 dígitos.';
                        break;
                    case 'auth/user-not-found':
                    case 'auth/invalid-credential':
                        msg = 'Las credenciales son incorrectas. Por favor verifica tus datos.';
                        break;
                    case 'auth/user-disabled':
                        msg = 'Tu cuenta ha sido deshabilitada. Contacta al administrador para más información.';
                        break;
                    case 'auth/too-many-requests':
                        msg = 'Demasiados intentos fallidos. Por seguridad, el acceso ha sido bloqueado temporalmente.';
                        break;
                    case 'auth/network-request-failed':
                        const offlineResult = await verifyOfflineCredentials(email, password);
                        if (offlineResult && !offlineResult.error) {
                            setOfflineUser(offlineResult.uid, offlineResult.email);
                            showNotification('Sesión iniciada en modo local (sin internet).', 'success');
                            navigate('/vigilancia', { replace: true });
                            return;
                        }
                        
                        if (offlineResult?.error === 'no-vault') {
                            msg = 'No tienes conexión a internet y no hay sesión guardada en este equipo.';
                        } else if (offlineResult?.error === 'wrong-user') {
                            msg = 'Estás sin internet y el usuario ingresado no coincide con la última sesión guardada.';
                        } else if (offlineResult?.error === 'wrong-password') {
                            msg = 'Contraseña incorrecta para el modo offline.';
                        } else {
                            msg = 'Error de conexión. Por favor verifica tu conexión a internet.';
                        }
                        break;
                    default:
                        if (error.message) {
                            msg = error.message.replace('Firebase: ', '').replace(/\(auth\/[^)]+\)\.?/, '').trim() || msg;
                        }
                }
            }

            showNotification(msg, 'error');
        }
    };

    return (
        <form
            onSubmit={handleSignIn}
            className="login-form"
            autoComplete="on"
            method="post"
            action="/login"
            noValidate
        >
            <h2>¡Bienvenido!</h2>
            <p>Ingresa a tu cuenta para continuar</p>

            <div className="form-group">
                <div className="label-with-icon">
                    <FaEnvelope className="label-icon" />
                    <label htmlFor="login-email">Usuario / Correo</label>
                </div>
                <input
                    type="text"
                    name="email"
                    id="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email o teléfono (10 dígitos)"
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="on"
                    inputMode="text"
                    spellCheck="true"
                    data-lpignore="false"
                    readOnly={false}
                    onFocus={(e) => e.target.removeAttribute('readonly')}
                />
            </div>

            <div className="form-group">
                <div className="label-with-icon">
                    <FaLock className="label-icon" />
                    <label htmlFor="login-password">Contraseña</label>
                </div>
                <div className="password-wrapper">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ingresa tu contraseña"
                        required
                        aria-label="Contraseña"
                        autoComplete="current-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="text"
                        spellCheck="false"
                        data-lpignore="false"
                        readOnly={false}
                        onFocus={(e) => e.target.removeAttribute('readonly')}
                    />
                    <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-pressed={showPassword}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>
            </div>

            <div className="options-row">
                <label className="remember-me">
                    <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    Recordarme
                </label>
                <button
                    type="button"
                    onClick={() => onSetView('resetPassword')}
                    className="auth-switch-link"
                >
                    ¿Olvidaste tu contraseña?
                </button>
            </div>

            <button type="submit" className="btn-submit">Iniciar Sesión</button>

            <div className="auth-switch">
                <span>¿Quieres administrar tu fraccionamiento con nosotros?</span>
                <button
                    type="button"
                    onClick={() => onSetView('requestAccess')}
                    className="auth-switch-link"
                >
                    Solicita información aquí
                </button>
            </div>
        </form>
    );
};

const PasswordResetForm = ({ onSetView }) => {
    const [resetEmail, setResetEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const { showNotification } = useNotification();

    const handleResetPassword = async (e) => {
        e.preventDefault();
        const emailOrPhone = resetEmail.trim();

        if (!emailOrPhone) {
            showNotification('Por favor ingresa tu correo o número de teléfono.', 'error');
            return;
        }

        setLoading(true);
        console.log('[Login] Solicitando restablecimiento vía Cloud Function para:', emailOrPhone);

        try {
            const functions = getFunctions();
            const publicRequestPasswordReset = httpsCallable(functions, 'publicRequestPasswordReset');

            const result = await publicRequestPasswordReset({ emailOrPhone });

            if (result.data && result.data.success) {
                showNotification(result.data.message || 'Se ha enviado un enlace a tu correo.', 'success');
                onSetView('signIn');
            } else {
                throw new Error(result.data.message || 'Error al solicitar restablecimiento');
            }
        } catch (error) {
            console.error('[Login] ERROR al solicitar restablecimiento:', error);
            
            let userMessage = 'No se pudo procesar la solicitud. ';
            const errorMsg = error.message.toLowerCase();
            
            if (errorMsg.includes('not-found')) {
                userMessage = 'No encontramos una cuenta asociada a ese dato.';
            } else if (errorMsg.includes('invalid-argument')) {
                userMessage = 'El formato ingresado no es válido.';
            } else {
                userMessage += error.message;
            }
            
            showNotification(userMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleResetPassword} className="login-form">
            <h2>Restablecer Contraseña</h2>
            <p>Ingresa tu correo o teléfono de 10 dígitos para recibir un enlace de recuperación.</p>
            <div className="form-group">
                <div className="label-with-icon">
                    <FaUser className="label-icon" />
                    <label>Correo o Teléfono</label>
                </div>
                <input
                    type="text"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Email o teléfono (10 dígitos)"
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="on"
                    spellCheck="true"
                />
            </div>
            <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar Enlace'}
            </button>
            <div className="auth-switch">
                <button type="button" onClick={() => onSetView('signIn')} className="auth-switch-link">
                    Volver a Iniciar Sesión
                </button>
            </div>
        </form>
    );
};

// --- Sub-componente para Solicitar Registro ---
const RequestAccessForm = ({ onSetView }) => {
    const [formData, setFormData] = useState({
        nombreAdmin: '',
        nombreFracc: '',
        numLotes: '',
        email: '',
        celular: ''
    });
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);

    const handleChange = (e) => {
        if (submissionStatus) {
            setSubmissionStatus(null);
        }
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            const functions = getFunctions();
            const submitDemoRequest = httpsCallable(functions, 'submitDemoRequest');

            await submitDemoRequest({
                nombreAdmin: formData.nombreAdmin,
                nombreFracc: formData.nombreFracc,
                numLotes: parseInt(formData.numLotes) || 0,
                email: formData.email,
                celular: formData.celular
            });

            const feedbackMessage = '¡Gracias! Tu solicitud ha sido enviada. Pronto un asesor te contactará.';
            setSubmissionStatus({ type: 'success', message: feedbackMessage });
            showNotification(feedbackMessage, 'success');
            setTimeout(() => onSetView('signIn'), 2200);
        } catch (error) {
            const message = error?.message || 'Hubo un error al enviar tu solicitud.';
            console.error('RequestAccessForm: error al enviar solicitud', error);
            setSubmissionStatus({ type: 'error', message });
            showNotification(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="login-form" noValidate>
            <h2>Solicita una Demo</h2>
            <p>Déjanos tus datos y un asesor se pondrá en contacto contigo.</p>
            {submissionStatus && (
                <div className={`form-alert form-alert-${submissionStatus.type}`} role="status">
                    <strong>{submissionStatus.type === 'success' ? '¡Solicitud enviada!' : 'Aviso importante'}</strong>
                    <span>{submissionStatus.message}</span>
                </div>
            )}
            <div className="form-group">
                <label>Nombre Completo</label>
                <div className="input-wrapper">
                    <input
                        type="text"
                        name="nombreAdmin"
                        value={formData.nombreAdmin}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>
            <div className="form-group">
                <label>Nombre de tu Fraccionamiento</label>
                <div className="input-wrapper">
                    <input
                        type="text"
                        name="nombreFracc"
                        value={formData.nombreFracc}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>
            <div className="form-group">
                <label>Número de Lotes/Casas</label>
                <div className="input-wrapper">
                    <input
                        type="number"
                        name="numLotes"
                        value={formData.numLotes}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>
            <div className="form-group">
                <div className="label-with-icon">
                    <FaEnvelope className="label-icon" />
                    <label>Correo Electrónico de Contacto</label>
                </div>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@empresa.com"
                    required
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="on"
                    spellCheck="true"
                    readOnly={false}
                    onFocus={(e) => e.target.removeAttribute('readonly')}
                />
            </div>
            <div className="form-group">
                <label>Número de Celular</label>
                <div className="input-wrapper">
                    <input
                        type="tel"
                        name="celular"
                        value={formData.celular}
                        onChange={handleChange}
                        placeholder="10 dígitos"
                        pattern="[0-9]{10}"
                        required
                    />
                </div>
            </div>
            <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Enviando...' : 'Enviar Solicitud'}
            </button>
            <div className="auth-switch">
                <button type="button" onClick={() => onSetView('signIn')} className="auth-switch-link">
                    Volver a Iniciar Sesión
                </button>
            </div>
        </form>
    );
};

// --- Componente Principal de Login ---
const Login = () => {
    // Verificar si viene desde la landing page con parámetro de solicitud de demo
    const urlParams = new URLSearchParams(window.location.search);
    const initialView = urlParams.get('view') === 'requestAccess' ? 'requestAccess' : 'signIn';
    const [view, setView] = useState(initialView); // 'signIn' | 'resetPassword' | 'requestAccess'

    // Estado del tema leído desde localStorage (Tema claro por defecto)
    const [theme] = useState(() => localStorage.getItem('theme') || 'light');

    // Estado para las coordenadas del cursor (efecto parallax)
    const [coords, setCoords] = useState({ x: 0, y: 0 });

    useEffect(() => {
        document.title = 'Domus Habita - Acceso de Usuarios';
    }, []);

    // Capturar el movimiento del ratón
    const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const x = (clientX - window.innerWidth / 2) / 50;
        const y = (clientY - window.innerHeight / 2) / 50;
        setCoords({ x, y });
    };

    return (
        <div 
            className={`login-page-wrapper ${theme === 'dark' ? 'theme-dark' : ''}`}
            onMouseMove={handleMouseMove}
        >
            {/* Capas del Fondo Parallax */}
            <div className="landing-bg-parallax">
                <div 
                    className="parallax-city-silhouette" 
                    style={{ 
                        transform: `translate3d(${coords.x * 0.3}px, 0, 0)` 
                    }}
                />
                <div 
                    className="parallax-triangles-grid"
                    style={{ 
                        transform: `translate3d(${coords.x * 0.7}px, ${coords.y * 0.7}px, 0)` 
                    }}
                >
                    <div className="geo-triangle triangle-1"></div>
                    <div className="geo-triangle triangle-2"></div>
                    <div className="geo-triangle triangle-3"></div>
                    <div className="geo-triangle triangle-4"></div>
                    <div className="geo-triangle triangle-5"></div>
                    <div className="geo-triangle triangle-6"></div>
                    <div className="geo-triangle triangle-7"></div>
                    <div className="geo-triangle triangle-8"></div>
                    <div className="geo-triangle triangle-9"></div>
                    <div className="geo-triangle triangle-10"></div>
                    <div className="geo-triangle triangle-11"></div>
                    <div className="geo-triangle triangle-12"></div>
                    <div className="geo-triangle triangle-13"></div>
                    <div className="geo-triangle triangle-14"></div>
                    <div className="geo-triangle triangle-15"></div>
                </div>
            </div>

            <div className="login-container">
                {/* Panel Izquierdo - Branding con Parallax en blanco traslúcido */}
                <div className="login-left-panel">
                    <div className="branding-content">
                        <div className="brand-icon">
                            <img src="/logo.png" alt="DOMUS HABITA" className="brand-logo" />
                        </div>
                        <h1 className="brand-title">DOMUS HABITA</h1>
                        <p className="brand-tagline">Gestión Inmobiliaria Inteligente</p>

                        <div className="brand-section">
                            <h2 className="brand-section-title">Soluciones Tecnológicas</h2>
                            <p className="brand-description">
                                Administra tu fraccionamiento de forma sencilla, moderna y segura.
                            </p>
                        </div>

                        <ul className="brand-features">
                            <li>
                                <FaCheckCircle className="feature-icon" />
                                <span>Control total de residentes y propiedades</span>
                            </li>
                            <li>
                                <FaCheckCircle className="feature-icon" />
                                <span>Gestión financiera en tiempo real</span>
                            </li>
                            <li>
                                <FaCheckCircle className="feature-icon" />
                                <span>Vigilancia y control de acceso inteligente</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Panel Derecho - Formulario con Glassmorphism */}
                <div className="login-right-panel">
                    <div className="login-form-container">
                        {view === 'resetPassword' && <PasswordResetForm onSetView={setView} />}
                        {view === 'requestAccess' && <RequestAccessForm onSetView={setView} />}
                        {view === 'signIn' && <SignInForm onSetView={setView} />}
                    </div>
                    <p className="login-copyright">© 2026 Domus Habita. Todos los derechos reservados.</p>
                </div>
            </div>
        </div>
    );
};

export default Login;
